import React from 'react';
import styled from 'styled-components';
import { useTimer } from '../hooks';

const Wrapper = styled.div`
  width: 100%;
`;

const LabelRow = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 20px;
  margin-bottom: 8px;
  padding: 0 48px;
`;

const ScanLabel = styled.span<{ $dark?: boolean }>`
  font-size: 13px;
  font-weight: 700;
  color: ${p => (p.$dark ? 'rgba(127, 224, 196, 0.95)' : '#14b8a6')};
  text-align: center;
  transition: all 0.3s ease;
`;

const PercentLabel = styled.span<{ $dark?: boolean }>`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 13px;
  font-weight: 700;
  color: ${p => (p.$dark ? 'rgba(255, 255, 255, 0.55)' : '#64748b')};
`;

const BarTrack = styled.div<{ $dark?: boolean }>`
  width: 100%;
  height: 6px;
  background: ${p => (p.$dark ? 'rgba(255, 255, 255, 0.15)' : '#e2e8f0')};
  border-radius: 999px;
  overflow: hidden;
`;

const BarFill = styled.div<{ pct: number }>`
  height: 100%;
  width: ${({ pct }) => pct}%;
  background: linear-gradient(90deg, #14b8a6, #0891b2);
  border-radius: 999px;
  transition: width 0.9s linear;
`;

const MESSAGES = [
  'Measuring blood pressure...',
  'Analyzing pulse rate...',
  'Calculating stress levels...',
  // 'Checking oxygen saturation...', // SpO2 disabled — web SDK
  'Processing wellness index...',
  'Detecting heart rate variability...',
  'Finalizing health insights...',
];

const Timer = ({ started, durationSeconds, variant = 'light' }: {
  started: boolean;
  durationSeconds: number;
  variant?: 'light' | 'dark';
}) => {
  const dark = variant === 'dark';
  const seconds = useTimer(started, durationSeconds);
  const pct = started ? Math.min(100, Math.round((seconds / durationSeconds) * 100)) : 0;
  
  const [msgIdx, setMsgIdx] = React.useState(0);

  React.useEffect(() => {
    if (!started) {
      setMsgIdx(0);
      return;
    }
    const interval = setInterval(() => {
      setMsgIdx(prev => (prev + 1) % MESSAGES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [started]);

  return (
    <Wrapper>
      <LabelRow>
        <ScanLabel $dark={dark}>
          {started ? MESSAGES[msgIdx] : 'Ready to scan'}
        </ScanLabel>
        {started && <PercentLabel $dark={dark}>{pct}%</PercentLabel>}
      </LabelRow>
      <BarTrack $dark={dark}>
        <BarFill pct={pct} />
      </BarTrack>
    </Wrapper>
  );
};

export default Timer;
